import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import ChartBaseComponent from 'src/app/components/shared/kpi/chart.base';
import { Ng2ChartConfiguration } from 'src/app/components/shared/kpi/chart.model';
import { KpiScale } from 'src/app/models/kpiform';
import { KpiFamily } from 'src/app/services/kpi.service';

enum DatasetColors {
  in = 'orange',
  out = 'blue',
}

@Component({
    selector: 'app-bar-family-in-out',
    templateUrl: '../../../shared/kpi/chart-card.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BarFamilyInOutComponent extends ChartBaseComponent<KpiFamily, 'bar', string> implements OnChanges {

  @Input() scale: KpiScale;

  public chartConfiguration: Ng2ChartConfiguration<'bar', number[], string> = {
    type: 'bar',
    data: { datasets: [] },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          position: 'top',
        },
        legend: {
          display: true,
          position: 'bottom',
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        x: {
          stacked: true,
          display: true,
          beginAtZero: true,
          grid: {
            display: false
          }
        },
        y: {
          stacked: true,
          display: true,
        },
      },
    },
  };

  private _unit: string;

  ngOnChanges(changes: SimpleChanges): void {
    // Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    // Add '${implements OnChanges}' to the class.
    super.ngOnChanges(changes);
    if (changes.scale) {
      if (changes.scale.currentValue !== changes.scale.previousValue) {
        this._handleScaleChange();
        this._cd.markForCheck();
      }
    }
  }

  /**
   * Handle a change of scope
   * Set the values relative to percentage or values
   */
  private _handleScaleChange() {
    this._unit = this.scale === 'percent' ? '%' : this._translate.instant('HOURS');
    this.chartConfiguration.options.scales.y.ticks = this.scale === 'percent' ? { callback(value) { return value + '%'; } } : undefined;
    if (this.dataKPI) {
      this.chartConfiguration.options.plugins.title.text = `${this._translate.instant('TIME_IN_OUT_ZONE_FAMILY', { zoneName: this.dataKPI.familyInZone })} (${this._unit})`;
      this.setKpiData();
    }
  }

  /**
   * Get the percentage of time relative to all times (static + moving + unknown)
   * @param time The time value
   * @returns The percentage
   */
  private _getPercentageOfTime(time: number): number {
    const sum = this.dataKPI.familyInTime + this.dataKPI.familyOutTime;
    return +(time / sum * 100).toFixed(2);
  }

  setKpiData(): void {
    this.chartConfiguration.data = {
      labels: [this.dataKPI.familyName],
      datasets: [
        {
          label: `${this._translate.instant('TIME_IN_ZONE')} (${this._unit})`,
          backgroundColor: DatasetColors.in,
          data: [
            this.scale === 'percent' ? this._getPercentageOfTime(this.dataKPI.familyInTime) : this.dataKPI.familyInTime
          ],
          borderColor: DatasetColors.in,
          borderWidth: 1,
        },
        {
          label: `${this._translate.instant('TIME_OUT_ZONE')} (${this._unit})`,
          backgroundColor: DatasetColors.out,
          data: [
            this.scale === 'percent' ? this._getPercentageOfTime(this.dataKPI.familyOutTime) : this.dataKPI.familyOutTime
          ],
          borderColor: DatasetColors.out,
          borderWidth: 1,
        },
      ]
    };
  }

}
